/**
 * PersonaEngineInstances — Tenant-wide persona instance management page.
 *
 * Displays all active Persona Engine persona instances across the tenant with:
 *   1. Summary cards (total, stage distribution, avg dimensions)
 *   2. Instance table with stage badges
 *   3. Expandable row detail with graduation dimension BarChart
 *
 * Route: /tenant/engage/persona-engine-instances
 */

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronRight, Layers, Users, BarChart3, Activity } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { PageHeader, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { usePersonaEngineTenantInstances, usePersonaEngineGraduation } from '@/hooks/usePersonaEngine'
import { formatDistanceToNow } from 'date-fns'

// ─── Constants ───────────────────────────────────────────────────────────────

const STAGE_BADGE_CLASSES: Record<string, string> = {
  base: 'bg-gray-100 text-gray-600',
  emerging: 'bg-blue-100 text-blue-700',
  established: 'bg-green-100 text-green-700',
  mature: 'bg-purple-100 text-purple-700',
}

const STAGE_LABELS: Record<string, string> = {
  base: 'Base',
  emerging: 'Emerging',
  established: 'Established',
  mature: 'Mature',
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function InstancesSkeleton() {
  return (
    <div className="max-w-5xl space-y-6">
      {/* Summary cards skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-warm-200 bg-white p-5 animate-pulse">
            <div className="h-3 w-20 bg-warm-100 rounded mb-3" />
            <div className="h-6 w-12 bg-warm-100 rounded" />
          </div>
        ))}
      </div>
      {/* Table skeleton */}
      <div className="rounded-xl border border-warm-200 bg-white animate-pulse">
        <div className="p-4 border-b border-warm-100">
          <div className="h-4 w-32 bg-warm-100 rounded" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-4 border-b border-warm-100">
            <div className="h-4 w-4 bg-warm-100 rounded" />
            <div className="h-4 w-40 bg-warm-100 rounded" />
            <div className="h-4 w-24 bg-warm-100 rounded" />
            <div className="h-5 w-16 bg-warm-100 rounded-full" />
            <div className="h-4 w-8 bg-warm-100 rounded" />
            <div className="flex-1" />
            <div className="h-4 w-20 bg-warm-100 rounded" />
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Row Detail with Graduation BarChart ─────────────────────────────────────

function InstanceDetail({ personaId }: { personaId: string }) {
  const { data: graduation, isLoading } = usePersonaEngineGraduation(personaId)

  if (isLoading) {
    return (
      <div className="p-6 bg-gray-50 border-t border-warm-100 animate-pulse">
        <div className="h-4 w-48 bg-warm-100 rounded mb-4" />
        <div className="h-48 w-full bg-warm-100 rounded" />
      </div>
    )
  }

  if (!graduation) {
    return (
      <div className="p-6 bg-gray-50 border-t border-warm-100">
        <p className="text-sm text-gray-500">No graduation data available.</p>
      </div>
    )
  }

  const chartData = (graduation.dimension_details ?? []).map((d) => ({
    dimension: d.dimension.length > 15
      ? d.dimension.slice(0, 14) + '...'
      : d.dimension,
    fullName: d.dimension,
    value: d.current_value ?? d.archetype_default ?? 0,
    source: d.source,
    isDerived: d.is_profile_derived,
  }))

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.25 }}
      className="overflow-hidden"
    >
      <div className="p-6 bg-gray-50 border-t border-warm-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Graduation Dimensions
            </h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {graduation.dimensions_derived} of {graduation.total_dimensions} dimensions profile-derived
            </p>
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${STAGE_BADGE_CLASSES[graduation.stage] ?? STAGE_BADGE_CLASSES.base}`}>
            {STAGE_LABELS[graduation.stage] ?? graduation.stage}
          </span>
        </div>

        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 28)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 0, right: 20, bottom: 0, left: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 1]} tickCount={6} fontSize={11} />
              <YAxis
                type="category"
                dataKey="dimension"
                width={120}
                fontSize={11}
                tick={{ fill: '#6b7280' }}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null
                  const d = payload[0].payload
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
                      <p className="font-semibold text-gray-900 mb-1">{d.fullName}</p>
                      <p className="text-gray-600">Value: {d.value.toFixed(3)}</p>
                      <p className="text-gray-600">Source: {d.source}</p>
                    </div>
                  )
                }}
              />
              <Bar
                dataKey="value"
                radius={[0, 4, 4, 0]}
                fill="#6366f1"
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">No dimension data available.</p>
        )}
      </div>
    </motion.div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function PersonaEngineInstances() {
  const breadcrumbs = buildBreadcrumbs('/tenant/engage/persona-engine-instances')
  const { data: instances, isLoading } = usePersonaEngineTenantInstances()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Summary stats
  const stats = useMemo(() => {
    if (!instances || instances.length === 0) {
      return { total: 0, base: 0, emerging: 0, established: 0, mature: 0, avgDimensions: 0 }
    }

    const counts = { base: 0, emerging: 0, established: 0, mature: 0 }
    let totalDimensions = 0

    for (const inst of instances) {
      const stage = inst.graduation_stage as keyof typeof counts
      if (stage in counts) counts[stage]++
      totalDimensions += inst.graduation_dimensions_derived
    }

    return {
      total: instances.length,
      ...counts,
      avgDimensions: instances.length > 0
        ? Math.round((totalDimensions / instances.length) * 10) / 10
        : 0,
    }
  }, [instances])

  const toggleExpand = (personaId: string) => {
    setExpandedId((prev) => (prev === personaId ? null : personaId))
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Persona Instances"
          description="View and manage active persona instances across the tenant."
          breadcrumbs={breadcrumbs}
        />
        <InstancesSkeleton />
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Persona Instances"
        description="View and manage active persona instances across the tenant."
        breadcrumbs={breadcrumbs}
      />

      <div className="max-w-5xl space-y-6">
        {/* Summary Cards */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <div className="rounded-xl border border-warm-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-personic-500" />
              <p className="text-xs text-gray-500 font-medium">Total Instances</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>

          <div className="rounded-xl border border-warm-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-500" />
              <p className="text-xs text-gray-500 font-medium">Stage Distribution</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(['base', 'emerging', 'established', 'mature'] as const).map((stage) => (
                <span
                  key={stage}
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${STAGE_BADGE_CLASSES[stage]}`}
                >
                  {stats[stage]} {STAGE_LABELS[stage]}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-warm-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-indigo-500" />
              <p className="text-xs text-gray-500 font-medium">Avg Dimensions</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.avgDimensions}</p>
          </div>

          <div className="rounded-xl border border-warm-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-green-500" />
              <p className="text-xs text-gray-500 font-medium">Mature Rate</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {stats.total > 0
                ? `${Math.round((stats.mature / stats.total) * 100)}%`
                : '0%'}
            </p>
          </div>
        </motion.div>

        {/* Instance Table */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.1 }}
        >
          <div className="rounded-xl border border-warm-200 bg-white overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[2rem_1fr_1fr_6rem_4rem_6rem] gap-4 px-4 py-3 border-b border-warm-100 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <div />
              <div>Name</div>
              <div>Archetype</div>
              <div>Stage</div>
              <div className="text-center">Dims</div>
              <div className="text-right">Created</div>
            </div>

            {/* Table Body */}
            {!instances || instances.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No active persona instances found.</p>
                <p className="text-xs mt-1">Instances are created when users adopt archetypes.</p>
              </div>
            ) : (
              <div>
                {instances.map((inst) => {
                  const isExpanded = expandedId === inst.persona_id
                  const stage = inst.graduation_stage ?? 'base'
                  const badgeClass = STAGE_BADGE_CLASSES[stage] ?? STAGE_BADGE_CLASSES.base
                  const stageLabel = STAGE_LABELS[stage] ?? stage

                  return (
                    <div key={inst.persona_id}>
                      <button
                        type="button"
                        onClick={() => toggleExpand(inst.persona_id)}
                        className="w-full grid grid-cols-[2rem_1fr_1fr_6rem_4rem_6rem] gap-4 items-center px-4 py-3 border-b border-warm-100 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="text-gray-400">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {inst.instance_name ?? 'Unnamed'}
                          </p>
                          <p className="text-xs text-gray-400 truncate font-mono">
                            {inst.product_context}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-gray-600 truncate">
                            {inst.archetype_id ?? 'N/A'}
                          </p>
                          {inst.archetype_category && (
                            <p className="text-xs text-gray-400 truncate">
                              {inst.archetype_category}
                            </p>
                          )}
                        </div>
                        <div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${badgeClass}`}>
                            {stageLabel}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="text-sm font-mono text-gray-700">
                            {inst.graduation_dimensions_derived}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-500">
                            {inst.created_at
                              ? formatDistanceToNow(new Date(inst.created_at), { addSuffix: true })
                              : '--'}
                          </span>
                        </div>
                      </button>

                      {/* Expandable detail */}
                      <AnimatePresence>
                        {isExpanded && (
                          <InstanceDetail personaId={inst.persona_id} />
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}
